import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createIssue, getProperties, getApartments, getEmergencyContacts, getSuppliers, Property, Apartment, Supplier, EmergencyContact } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft,
  Phone,
  Siren,
  CheckCircle,
  Truck,
  House
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { FileUpload } from '../components/FileUpload';
import { toast } from 'sonner';

export default function NewIssuePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>([]);
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    category: string;
    urgency_level: number;
    property_id: string;
    apartment_id: string;
    assigned_type: 'internal' | 'supplier';
    supplier_id: string;
    attachments: string[];
  }>({
    title: '',
    description: '',
    category: '',
    urgency_level: 2,
    property_id: '',
    apartment_id: '',
    assigned_type: 'internal',
    supplier_id: '',
    attachments: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [propsRes, contactsRes, suppliersRes] = await Promise.all([
          getProperties(),
          getEmergencyContacts(),
          getSuppliers()
        ]);
        setProperties(propsRes.data);
        setEmergencyContacts(contactsRes.data);
        setSuppliers(suppliersRes.data);
        
        // Set default property if only one exists
        if (propsRes.data.length === 1) {
          setFormData(prev => ({ ...prev, property_id: propsRes.data[0].id }));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    
    fetchData();
  }, []);

  // Fetch apartments when property changes
  useEffect(() => {
    const fetchApartments = async () => {
      if (!formData.property_id) {
        setApartments([]);
        return;
      }
      
      try {
        const { data } = await getApartments({ property_id: formData.property_id });
        // Sort apartments by apartment_number (natural sort: 2A, 2B, 10A, 10B)
        const sorted = [...data].sort((a, b) => {
          const numA = a.apartment_number || '';
          const numB = b.apartment_number || '';
          return numA.localeCompare(numB, 'sv', { numeric: true });
        });
        setApartments(sorted);
      } catch (error) {
        console.error('Failed to fetch apartments:', error);
      }
    };
    
    fetchApartments();
  }, [formData.property_id]);

  const handleChange = (field: string, value: string | number | string[]): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Reset apartment when property changes
    if (field === 'property_id') {
      setFormData(prev => ({ ...prev, apartment_id: '' }));
    }
    
    // Reset supplier when assignment type changes
    if (field === 'assigned_type' && value === 'internal') {
      setFormData(prev => ({ ...prev, supplier_id: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category || !formData.property_id) {
      toast.error('Vänligen fyll i alla obligatoriska fält');
      return;
    }
    
    setLoading(true);
    try {
      const issueData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        urgency_level: formData.urgency_level,
        property_id: formData.property_id,
        apartment_id: formData.apartment_id && formData.apartment_id !== 'none' ? formData.apartment_id : undefined,
        assigned_type: formData.assigned_type as 'internal' | 'supplier',
        supplier_id: formData.assigned_type === 'supplier' ? formData.supplier_id : undefined,
        attachments: formData.attachments,
        organization_id: user?.organization_id || undefined,
        reporter_email: user?.email || undefined,
        reporter_name: user?.name || undefined
      };
      await createIssue(issueData, { 
        sendNotification: true, 
        organizationId: user?.organization_id 
      });
      toast.success('Ärende registrerat');
      navigate('/issues');
    } catch (error) {
      toast.error(t('error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  const getEmergencyContact = () => {
    const categoryContact = emergencyContacts.find(c => c.category === formData.category);
    return categoryContact || emergencyContacts.find(c => c.category === 'general');
  };

  const urgencyLevels = [
    { value: 1, label: t('urgency_1'), color: 'bg-green-500' },
    { value: 2, label: t('urgency_2'), color: 'bg-lime-500' },
    { value: 3, label: t('urgency_3'), color: 'bg-yellow-500' },
    { value: 4, label: t('urgency_4'), color: 'bg-orange-500' },
    { value: 5, label: t('urgency_5'), color: 'bg-red-500' }
  ];

  const emergencyContact = getEmergencyContact();

  // Filter suppliers by category if a category is selected
  const filteredSuppliers = formData.category 
    ? suppliers.filter(s => s.category === formData.category || s.category === 'other')
    : suppliers;

  return (
    <div className="p-6 md:p-8 max-w-2xl" data-testid="new-issue-page">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-4"
          data-testid="back-button"
        >
          <ArrowLeft size={16} />
          {t('back')}
        </button>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Registrera ärende
        </h1>
      </div>

      {/* Emergency Warning for high urgency */}
      {formData.urgency_level >= 4 && emergencyContact && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md animate-fade-in">
          <div className="flex items-start gap-3">
            <Siren size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">
                {t('emergency_warning')}
              </h3>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-sm text-red-700">{emergencyContact.name}:</span>
                <a
                  href={`tel:${emergencyContact.phone}`}
                  className="flex items-center gap-1 text-sm font-mono font-medium text-red-800 hover:underline"
                  data-testid="emergency-call-button"
                >
                  <Phone size={14} />
                  {emergencyContact.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card-flat space-y-6">
          {/* Property */}
          <div className="space-y-2">
            <Label htmlFor="property">{t('properties')} *</Label>
            <Select
              value={formData.property_id}
              onValueChange={(value) => handleChange('property_id', value)}
            >
              <SelectTrigger data-testid="property-select">
                <SelectValue placeholder="Välj fastighet" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name} - {prop.address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Apartment */}
          <div className="space-y-2">
            <Label htmlFor="apartment">Lägenhet</Label>
            <Select
              value={formData.apartment_id}
              onValueChange={(value) => handleChange('apartment_id', value)}
              disabled={!formData.property_id || apartments.length === 0}
            >
              <SelectTrigger data-testid="apartment-select">
                <SelectValue placeholder={apartments.length === 0 ? "Inga lägenheter" : "Välj lägenhet (valfritt)"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ingen specifik lägenhet</SelectItem>
                {apartments.map((apt) => (
                  <SelectItem key={apt.id} value={apt.id}>
                    {apt.apartment_number} {apt.floor ? `(Vån ${apt.floor})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">{t('issue_title')} *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Kort beskrivning av ärendet"
              data-testid="issue-title-input"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>{t('issue_category')} *</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => handleChange('category', value)}
            >
              <SelectTrigger data-testid="category-select">
                <SelectValue placeholder="Välj kategori" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="plumbing">{t('plumbing')}</SelectItem>
                <SelectItem value="electrical">{t('electrical')}</SelectItem>
                <SelectItem value="heating">{t('heating')}</SelectItem>
                <SelectItem value="locksmith">Lås</SelectItem>
                <SelectItem value="elevator">Hiss</SelectItem>
                <SelectItem value="cleaning">Städ</SelectItem>
                <SelectItem value="garden">Trädgård/Mark</SelectItem>
                <SelectItem value="construction">Bygg/Snickeri</SelectItem>
                <SelectItem value="other">{t('other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Urgency Level */}
          <div className="space-y-3">
            <Label>{t('urgency_level')}</Label>
            <div className="flex flex-wrap gap-2">
              {urgencyLevels.map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => handleChange('urgency_level', level.value)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                    formData.urgency_level === level.value
                      ? 'border-slate-900 bg-slate-50'
                      : 'border-border hover:bg-slate-50'
                  }`}
                  data-testid={`urgency-${level.value}`}
                >
                  <div className={`w-2 h-2 rounded-full ${level.color}`} />
                  {level.label}
                  {formData.urgency_level === level.value && (
                    <CheckCircle size={14} className="text-slate-600" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('issue_description')} *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Beskriv ärendet i detalj..."
              rows={5}
              data-testid="issue-description-input"
            />
          </div>

          {/* Assignment */}
          <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-border">
            <Label className="text-base">Tilldelning</Label>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => handleChange('assigned_type', 'internal')}
                className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                  formData.assigned_type === 'internal'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-border bg-white hover:bg-slate-50'
                }`}
                data-testid="assign-internal"
              >
                <House size={20} />
                <span className="font-medium">Internt</span>
              </button>
              
              <button
                type="button"
                onClick={() => handleChange('assigned_type', 'supplier')}
                className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                  formData.assigned_type === 'supplier'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-border bg-white hover:bg-slate-50'
                }`}
                data-testid="assign-supplier"
              >
                <Truck size={20} />
                <span className="font-medium">Leverantör</span>
              </button>
            </div>
            
            {formData.assigned_type === 'internal' && (
              <p className="text-sm text-slate-500">
                Ärendet hanteras internt av föreningen.
              </p>
            )}
            
            {formData.assigned_type === 'supplier' && (
              <div className="space-y-2">
                <Label>Välj leverantör</Label>
                <Select
                  value={formData.supplier_id}
                  onValueChange={(value) => handleChange('supplier_id', value)}
                >
                  <SelectTrigger data-testid="supplier-select">
                    <SelectValue placeholder="Välj leverantör" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredSuppliers.length === 0 ? (
                      <SelectItem value="no-suppliers" disabled>Inga leverantörer i denna kategori</SelectItem>
                    ) : (
                      filteredSuppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {filteredSuppliers.length === 0 && formData.category && (
                  <p className="text-xs text-amber-600">
                    Ingen leverantör finns för denna kategori. Lägg till under Leverantörer.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>{t('attachments')}</Label>
            <FileUpload
              onUpload={(urls) => handleChange('attachments', urls)}
              folder="issues"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            data-testid="cancel-button"
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="btn-press"
            data-testid="submit-issue-button"
          >
            {loading ? t('loading') : 'Registrera'}
          </Button>
        </div>
      </form>
    </div>
  );
}
